<template>
  <oct-dialog
    full
    :title="customer ? `お客様検索` : `施術履歴`"
    :icon="'close'"
    @dismiss="$_octPrototypeView_goBack"
    @scroll="onScroll"
    class="ss-treatment-list"
  >
    <!-- Sorter -->
    <div class="ss-treatment-list__actions">
      <!-- Keyword -->
      <oct-text-field
        v-model="q"
        dense
        block
      >
        <template #leading-icon>
          <oct-icon icon="search" />
        </template>
      </oct-text-field>

      <div
        class="ss-treatment-list__action"
        @click="$router.push({name: 'Treatments.filter'})"
      >
        <oct-icon icon="tune" />
      </div>
    </div>

    <oct-list>
      <oct-list-item
        v-for="item in (customer ? collectionByCustomer : collection).items"
        :key="item.id"
        v-bind="$_sstlv_itemToListItem(item, false, false)"
        x-dense
        @click.native="customer ?
          $router.push({
            name: 'Customer',
            params: {
              customerId: item.customer
            }
          }).catch(() => {}) :
          $router.push({
            path: `${item.id}`
          }).catch(() => {})"
      >
        <!-- Meta -->
        <template #meta>
          <!-- Icons-->
          <div class="ss-treatment-list-item__meta">
            <!-- Secret Icon -->
            <oct-icon
              icon="search-off"
              v-if="$_octPrototypeView_isSecret(item.customer)"
            />

            <!-- Like Icon (Only for treatments) -->
            <oct-icon
              v-if="!customer"
              icon="favorite-filled"
              :active="item.likedAt !== null"
            />
          </div>

          <!-- Shared Status (Only for treatments) -->
          <oct-chip
            v-if="!customer"
            dense
            :selected="item.status != 4"
          >
            {{ item.status != 4 ? '未送信' : '送信済' }}
          </oct-chip>
        </template>

        <!-- Trailing icon -->
        <template #trailing-icon>
          <oct-icon icon="chevron-right"/>
        </template>
      </oct-list-item>
    </oct-list>

    <transition :name="transitionName">
      <router-view :key="$route.path" />
    </transition>
  </oct-dialog>
</template>

<script>
import OctIcon from '@/components/icon/OctIcon.vue'
import OctChip from '@/components/chips/OctChip.vue'
import OctList from '@/components/list/OctList.vue'
import OctTextField from '@/components/text-field/OctTextField.vue'
import OctListItem from '@/components/list/OctListItem.vue'
import OctDialog from '@/components/dialog/OctDialog.vue'
import OctPrototypeView from '@/mixins/OctPrototypeView.js'

export default {
  name: 'SSTreatmentListView',
  mixins: [
    OctPrototypeView
  ],
  components: {
    OctIcon,
    OctChip,
    OctTextField,
    OctList,
    OctListItem,
    OctDialog
  },
  props: {
    /** Whether to search customer */
    customer: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    /** Search keyword */
    q: '',
    /** Previous turbo mode to search */
    $_SSTLV_turboPrevious: 0,
    /** Previous counts to search */
    $_SSTLV_countPrevious: 0,
    /** Secret customers that I was in charge of */
    $_SSTLV_mySecretCustomers: [],
  }),
  computed: {
    // Stylist‘s ID
    id: {
      get () {
        return this.$store.state.treatments.filter.id.join(',')
      },
      set (val) {
        this.$store.commit('treatments/filter/state', {id:[val],page:1})
      }
    },
    // Sort order
    order: {
      get () {
        return this.$store.state.treatments.filter.sort
      },
      set (val) {
        this.$store.commit('treatments/filter/state', {sort:val,page:1})
      }
    },
    /**
     * Returns a filtered collection containing one item per customer.
     * Excludes secret customers that I wasn't in charge of and ensures each customer appears only once.
     * If the filter is not sat, no customer items are returned.
     * If the user is an administrator, all customers are returned.
     * 
     * @returns {Array} returns.items - Array of treatment items, one per customer
     */
    collectionByCustomer ()  {
      if (!this.$_sstlv_isFilterSat) return {items: []}

      const secretCustomerIds = this.$store.state.data.secretCustomers.map(customer => customer.id);
      const customerIds = {}

      const items = this.collection.items.filter(item => {
        // Filters only if the customer is not already shown and not secret
        // or the user is an administrator
        if (
          customerIds[item.customer] === undefined
          && (
              !secretCustomerIds.includes(item.customer)
              || this.$store.state.staffs.me.isAdministrator
              || this.$data.$_SSTLV_mySecretCustomers.includes(item.customer)
             )
        ) {
          customerIds[item.customer] = true
          return true
        }

        return false
      })

      return {items: items}
    },
    /** Searched items */
    collection: vm => vm.$store.getters['treatments/collection']('searched'),
    /** Collection has next items */
    hasNext: vm => vm.collection.items.length < vm.collection.total,
    $_sstlv_isFilterSat () {
      return this.$store.state.treatments.filter.id.length > 0
        || this.$store.state.treatments.filter.customers.length > 0
        || this.$store.state.treatments.filter.datestart !== ''
        || this.$store.state.treatments.filter.dateend !== ''
        || this.$store.state.treatments.filter.status !== ''
        || this.$data.q !== ''
    },
    /**
     * Whether to run search when the component is mounted.
     * Returns true if no customer is selected (always search), or if any filter
     * (id, customers, date range, status, or query) is applied when viewing a customer.
     * @returns {boolean}
     */
    shouldSearchOnMounted () {
      if (!this.customer) return true;
      return this.$_sstlv_isFilterSat;
    }
  },
  methods: {
    /** Get list item */
    $_sstlv_itemToListItem (item, wantsTime=false, useProfileIcon=true) {
      const listItem = this.itemToListItem(item, wantsTime, useProfileIcon)

      // If search query is sat and customer name doesn't match query, set the item hidden.
      listItem.hidden = this.q !== '' && listItem.title.indexOf(this.q) === -1

      return listItem
    },
    /** Scroll handler */
    onScroll (event) {
      // Scroll top is not enough to load next items or items are fully fetched
      if (this.processing || this.collection.items.length >= this.collection.total ||
          event.target.scrollTop + event.target.clientHeight + 100 < event.target.scrollHeight)
        return

      this.processing = true

      this.$store.commit('treatments/filter/state', {
        page: this.$store.state.treatments.filter.page + 1
      })
    },
    /**
     * Fetch secret customers that I was in charge of
     */
    $_sslv_getMySecretCustomers () {
      const secretCustomerIds = this.$store.state.data.secretCustomers.map(customer => customer.id);

      if (secretCustomerIds.length === 0) {
        this.$data.$_SSTLV_mySecretCustomers = [];
        return;
      }

      this.$store.dispatch('treatments/search', {
        filter: {
          ...this.$store.state.treatments.filter,
          id: this.$store.state.treatments.filter.id,
          customers: secretCustomerIds,
          count: 1000
        }
      }).then(response => {
        let customerIds = new Set(response.items.map(item => item.customer));
        this.$data.$_SSTLV_mySecretCustomers = Array.from(customerIds);
      })
    }
  },
  watch: {
    /** Search keyword */
    async q (value) {
      this.processing = true

      // If the search query is sat, search customers first and set customer IDs to the filter
      // Otherwise, set the customer to empty
      try {
        this.$store.commit('treatments/filter/state', {
          customers: value ?
            await this.$store.dispatch('customers/search', {name: value})
              .then(response => response.map(item => item.id)) : []
        })
      } catch {
        // Do nothing
      }

      // If the user is searching for a customer, set the ID to empty,
      // because in the customer search, all treatments are targeted.
      this.$store.dispatch('treatments/search', {
        filter: {
          ...this.$store.state.treatments.filter,
          page:1,
          id: this.customer ? [] : this.$store.state.treatments.filter.id
        }
      })
    },
    collection () {
      this.processing = false

      this.$nextTick(() => {
        // The container
        const container = this.$el.querySelector('.oct-list__content')

        // If has next items, search more
        if (!this.processing && this.hasNext && container && container.getBoundingClientRect().bottom < window.innerHeight) {
          this.processing = true

          // Search more
          this.$store.commit('treatments/filter/state', {
            page: this.$store.state.treatments.filter.page + 1
          })
        }
      })
    }
  },
  mounted () {
    // Store the previous count and turbo mode
    this.$data.$_SSTLV_turboPrevious = this.$store.state.treatments.filter.turbo
    this.$data.$_SSTLV_countPrevious = this.$store.state.treatments.filter.count

    // Set the count to 500 and turn on turbo mode
    this.$store.commit('treatments/filter/state', {
      count: 200,
      turbo: true
    })

    if (!this.$store.state.staffs.me.isAdministrator)
      this.$_sslv_getMySecretCustomers();

    // If needed to search, run search
    if (this.shouldSearchOnMounted) {
      /**
       * Checks if the current page in the treatments filter is not the first page.
       * If it is not the first page, it commits a mutation to reset the page to 1.
       * If it is the first page, it dispatches an action to search for treatments.
       */
      if (this.$store.state.treatments.filter.page !== 1)
        this.$store.commit('treatments/filter/state', {page: 1})
      else
        this.$store.dispatch('treatments/search')
    }
  },
  destroyed () {
    // Reset count, turbo mode and customer IDs
    this.$store.commit('treatments/filter/state', {
      customers: [],
      count:this.$data.$_SSTLV_countPrevious,
      turbo:this.$data.$_SSTLV_turboPrevious
    })
  }
}
</script>

<style scoped lang="scss">
@import "../components/theme/variables";
@import "../components/grid/variables";
@import "../components/typography/variables";
@import "../components/typography/mixins";

.ss-treatment-list {
  &::v-deep .oct-dialog__content {
    padding-top: oct-rem(32);
  }
}

.ss-treatment-list__actions {
  margin: 0 0 oct-rem(32);
  display: flex;
  align-items: center;
  column-gap: oct-rem(14);
  padding: 0;

  .oct-select {
    flex: 1 1 auto;
  }
}

.ss-treatment-list__action {
  flex: 0 0 oct-rem(36);
  height: oct-rem(36);
  cursor: pointer;
}

.ss-treatment-list-item__meta {
  margin: 0 oct-rem(16) 0 0;
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
  gap: oct-rem(8);

  .oct-icon {
    fill: $oct-theme--neutral-30;
    width: oct-rem(16);
    height: oct-rem(16);
  }

  &::v-deep .oct-icon--active {
    fill: $oct-theme--danger;
  }
}
</style>